import { useEffect, useState, useRef, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { decodeEventLog } from "viem";
import { useChallenge } from "@/hooks/useChallenge";
import { useStravaConnection } from "@/hooks/useStravaConnection";
import { useStravaConnectionResult } from "@/hooks/useStravaConnectionResult";
import { useChallengeContract } from "@/hooks/useChallengeContract";
import { useUSDCBalance } from "@/hooks/useUSDCBalance";
import { ChallengeStatsCard } from "@/components/ChallengeStatsCard";
import { ExpandableSection } from "@/components/ExpandableSection";
import { ConnectStravaPrompt } from "@/components/ConnectStravaPrompt";
import { InsufficientBalanceModal } from "@/components/InsufficientBalanceModal";
import { JoinErrorModal } from "@/components/JoinErrorModal";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ErrorToast } from "@/components/ErrorToast";
import { StickyHeader } from "@/components/ui/StickyHeader";
import { HeaderIconButton } from "@/components/ui/HeaderIconButton";
import { formatShortDate } from "@/utils/formatters";
import { useAuth } from "@/hooks/useAuth";
import {
  getChallengeCategory,
  getFirstSentence,
  getChallengeJoinability,
} from "@/utils/challengeHelpers";
import { DasherABI } from "@/config/contracts";
import { ALCHEMY_API_KEY } from "@/utils/constants";
import { refineErrorMessage } from "@/utils/errorMessages";
import { analytics } from "@/services/analytics";
import {
  isMockDiscoverChallenge,
  getMockDiscoverChallengeById,
} from "@/data/mockChallenges";

// USDC contract address on Base Mainnet
const USDC_ADDRESS = "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913";

// Admin wallet address for off-chain (marathon) challenge stakes
const ADMIN_WALLET_ADDRESS = "0xaada2850028ac902ec8fd6ee99394458878bedcf";

// Hardcoded mock data for visual elements (images, avatars)
const MOCK_HERO_IMAGE =
  "https://lh3.googleusercontent.com/aida-public/AB6AXuCroB4uAFk7TmmfRLvNR8SU7CP-AKhZ0d9ufPFX0wrgOXPCl6b7SU_QpAH0U0uXLbeJO-1Wb5HYkQyaQhorvDX42jjh0e_PPEDafe6vBe2SkXKFpZpqy8zjxMaTnQtcgqrhTVjB95dfbUFISOZcxoHtAAsHQDINlM36rJGtRbdf2008wLazPlk4wLiiwQmqKw7d47llSak3odnkP-u5pElSL88kdjNYFCoA4OznBn4pLAHVeXP-ALpVaeFJknXzYuBJTrnFqEIyXcS8";

const MOCK_PARTICIPANT_AVATARS = [
  "https://lh3.googleusercontent.com/aida-public/AB6AXuBfjy6guXGf3Ms0i_7R5JB-sKOf8HFZdQfmwofP8OhAzuZ1DjrK55n9fT2IHpRLk5ta3XjqukBDHNOZO4RzyAlnT0ldGg4-lhMymS8DL88zhQx3lr5CwiKhVD-E4kvJ85bt-RNfaVbU5Mukx3eLA4XT_SoVSLe34qDS3FhBEQTOKloxUHfIylqGpikQnDereV9Gvk6dkOTPa4lZ4z5TbJ_ZiKcaM8SvoZMUe2ONzMFwhEXJes_slYqmeKKBPX2HlFvtD-xoQizW5T00",
  "https://lh3.googleusercontent.com/aida-public/AB6AXuBabaBwUHoHPT2QxQkJpxdmcsngOnX5D3MyjpefYFLzDDvaFUP3h6dx9QOUdRqVZS7Fj1DL2ImkQx9zCR6th3nnPAdxgj2GR6IBgJmIhPH7Jfopw4XBSG4SLMpYjPx27T7rm4E1QZxoUsJwb1CN9dqAl7x-XUCVlLpM-nZ2LkHi_a0TnZDh6c7hosPnUWnV9c7atgn6Dl3RvLbJpFzixAlIxfZARJl5yowe24t60-GkZzswxk7ncWy_Ty-qqfSEywqHSWDatnBRF-P9",
  "https://lh3.googleusercontent.com/aida-public/AB6AXuCqnQnPz-0AvejlC4R1MECjakmzl-iAa8V1a_GLwxoTvAt089LFO6x6zrQTb-qVDh6eiuYYkkf31z7ROw3jb2ZtERtsxf56rIUMMcJH4t1fSQQ-izKH4iD5cenQi7bqF1BGkGTG-9TCOa-PTsgtivSglUJFyqXJRtkvsxooJoS3RiWdibBL4xBHUWnjTCqgiiGWVXCBR3I6K8t-D_KXPBueTRkcDY-3pApRNhbRugTGWAdfNduipG7_62VqpUlbtMqtammENLLhWYov",
];

// Helper: Format seconds to "Xh:XXm:XXs" format
const formatTargetTime = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${h}h:${m.toString().padStart(2, "0")}m:${s
    .toString()
    .padStart(2, "0")}s`;
};

// Helper: Generate "How it Works" based on challenge data
const getHowItWorks = (
  dailyGoal: number,
  durationDays: number,
  type: string | undefined | null,
  targetDurationSeconds?: number | null
): string[] => {
  const isMarathon = type === "marathon";

  // Marathon-specific "How it Works"
  if (isMarathon && targetDurationSeconds) {
    const targetTime = formatTargetTime(targetDurationSeconds);
    return [
      `Complete the full marathon (42.195km) within ${targetTime}.`,
      `Track your run via Strava — press log button in Dasher before the day ends.`,
      `Finish within the goal time to win your stake back + a share of the losers' pot.`,
      `Miss the target? Your stake goes to the winners' pool.`,
    ];
  }

  // Regular challenges (running/walking/cycling)
  const safeType = type || "run";
  const activityVerb =
    safeType === "run"
      ? "run"
      : safeType === "walk"
      ? "walk"
      : safeType === "cycle"
      ? "ride"
      : "complete your";

  return [
    `Complete your ${dailyGoal || 0}km ${activityVerb} goal every day for ${
      durationDays || 0
    } days.`,
    `Check in via the app to log your activity. Tracking is required for verification.`,
    `Miss a day, and your stake is added to the winner's pot.`,
    `Complete the challenge to get your stake back and a share of the pot!`,
  ];
};

export function ChallengePreview() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { privyUser } = useAuth();
  const {
    selectedChallenge,
    fetchChallenge,
    joinChallenge: joinChallengeInDB,
    isLoading,
    error,
  } = useChallenge();
  const { joinChallenge: joinChallengeOnChain, transferToWallet } =
    useChallengeContract();
  const { balance: walletBalance, isLoading: balanceLoading } =
    useUSDCBalance();
  const { showPrompt, ensureConnected, closePrompt, handleConnectionSuccess } =
    useStravaConnection();

  // Handle Strava OAuth callback result (shows toast, cleans URL)
  useStravaConnectionResult();

  // Local state for join flow
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [showJoinErrorModal, setShowJoinErrorModal] = useState(false);
  const [showInsufficientBalanceModal, setShowInsufficientBalanceModal] =
    useState(false);

  // Toast state for share feedback
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState<"success" | "error">(
    "success"
  );

  // Track if we've already tracked the view
  const hasTrackedView = useRef(false);

  // Check if this is a mock challenge
  const isMockChallenge = id ? isMockDiscoverChallenge(id) : false;
  const mockChallengeData = useMemo(
    () => (id ? getMockDiscoverChallengeById(id) : null),
    [id]
  );

  useEffect(() => {
    // Skip API call for mock challenges
    if (id && !isMockChallenge) {
      fetchChallenge(id);
    }
  }, [id, fetchChallenge, isMockChallenge]);

  // Track challenge viewed
  useEffect(() => {
    if (
      selectedChallenge &&
      selectedChallenge.id === id &&
      !hasTrackedView.current
    ) {
      hasTrackedView.current = true;
      analytics.track.challengeViewed({
        challengeId: selectedChallenge.id,
        challengeName: selectedChallenge.name,
        challengeType: selectedChallenge.type || "run",
        stakeAmount: selectedChallenge.stakeAmount,
        source: "discover",
      });
    }
  }, [selectedChallenge, id]);

  const handleShare = async () => {
    if (!id || !selectedChallenge) return;

    const shareUrl = `${window.location.origin}/challenge-preview/${id}`;
    const shareData = {
      title: selectedChallenge.name,
      text: `Join me in the "${selectedChallenge.name}" challenge on Dasher! Stake $${selectedChallenge.stakeAmount} and complete ${selectedChallenge.dailyGoal}km daily for ${selectedChallenge.durationDays} days.`,
      url: shareUrl,
    };

    // Check if Web Share API is supported
    const canShare = typeof navigator.share === "function";

    try {
      // Track share attempt
      analytics.track.shareInitiated({
        platform: canShare ? "native" : "copy_link",
        contentType: "challenge",
        challengeId: id,
      });

      // Use native share if available (includes "Copy" option in the share sheet)
      if (canShare) {
        await navigator.share(shareData);
      } else {
        // Fallback for browsers without Web Share API: copy to clipboard
        await navigator.clipboard.writeText(shareUrl);
        setToastMessage("Link copied to clipboard!");
        setToastVariant("success");
        setToastOpen(true);
      }
    } catch (error: any) {
      // User cancelled share or error occurred
      if (error.name !== "AbortError") {
        setToastMessage("Failed to share");
        setToastVariant("error");
        setToastOpen(true);
      }
    }
  };

  // For mock challenges, use the mock data directly
  // For real challenges, use the selectedChallenge from API
  const challenge = isMockChallenge ? mockChallengeData : selectedChallenge;

  // Error state (only for real challenges)
  if (!isMockChallenge && error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-background-light dark:bg-background-dark text-content-light dark:text-content-dark p-4">
        <p className="text-red-500 mb-4">{error}</p>
        <button onClick={() => navigate(-1)} className="text-primary underline">
          Go Back
        </button>
      </div>
    );
  }

  // Loading state (only for real challenges, mock challenges load instantly)
  if (
    !isMockChallenge &&
    (isLoading || !selectedChallenge || selectedChallenge.id !== id)
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background-light dark:bg-background-dark">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // If mock challenge data not found, go back
  if (isMockChallenge && !mockChallengeData) {
    navigate(-1);
    return null;
  }

  // At this point, challenge is guaranteed to exist
  if (!challenge) {
    return null;
  }

  // Use centralized joinability check - single source of truth
  const joinability = getChallengeJoinability(
    challenge,
    !!challenge.currentUserParticipant
  );

  const handleJoinChallenge = async () => {
    // Clear any previous errors
    setJoinError(null);

    // Prevent joining if not joinable (already joined, started, ended, etc.)
    if (!joinability.canJoin) {
      return;
    }

    // Check balance before proceeding
    if (walletBalance < challenge.stakeAmount) {
      setShowInsufficientBalanceModal(true);
      return;
    }

    // Check if Strava is connected before allowing join
    ensureConnected(async () => {
      if (!id || !challenge) {
        return;
      }

      try {
        setIsJoining(true);
        setJoinError(null);

        let stakePaid = challenge.stakeAmount;
        const isOffChain =
          (challenge as any).isOffChain || challenge.type === "marathon";

        if (isOffChain) {
          // ===== OFF-CHAIN JOIN (Marathon) =====
          // Direct transfer to admin wallet
          if (!ADMIN_WALLET_ADDRESS) {
            throw new Error(
              "Admin wallet not configured. Please try again later."
            );
          }

          const txResult = await transferToWallet({
            recipientAddress: ADMIN_WALLET_ADDRESS,
            tokenAddress: USDC_ADDRESS,
            amount: challenge.stakeAmount.toString(),
          });

          const txHash =
            txResult && typeof txResult === "object"
              ? (txResult as any).txnHash || (txResult as any).hash
              : null;

          if (!txHash) {
            throw new Error("Failed to get transaction hash");
          }

          // Wait for transaction confirmation
          const { createPublicClient, http } = await import("viem");
          const { base } = await import("viem/chains");

          const publicClient = createPublicClient({
            chain: base,
            transport: http(
              `https://base-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`
            ),
          });

          await publicClient.waitForTransactionReceipt({
            hash: txHash as `0x${string}`,
          });
        } else {
          // ===== ON-CHAIN JOIN (Running/Walking/Cycling) =====
          const txResult = await joinChallengeOnChain({
            challengeAddress: id,
            tokenAddress: USDC_ADDRESS,
            stakeAmount: challenge.stakeAmount.toString(),
          });

          // Extract transaction hash
          const txHash =
            txResult && typeof txResult === "object"
              ? (txResult as any).txnHash || (txResult as any).hash
              : null;

          if (!txHash) {
            throw new Error("Failed to get transaction hash");
          }

          // Wait for transaction receipt
          const { createPublicClient, http } = await import("viem");
          const { base } = await import("viem/chains");

          const publicClient = createPublicClient({
            chain: base,
            transport: http(
              `https://base-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`
            ),
          });

          const receipt = await publicClient.waitForTransactionReceipt({
            hash: txHash as `0x${string}`,
          });

          // Parse UserJoined event to get actual stake paid
          for (const log of receipt.logs) {
            try {
              const decodedLog = decodeEventLog({
                abi: DasherABI,
                data: log.data,
                topics: log.topics,
              });

              if (decodedLog.eventName === "UserJoined") {
                // Extract stake amount from event using named property (convert from wei to USDC)
                const eventArgs = decodedLog.args as any;
                const stakePaidWei = eventArgs._challengeStakeAmount;
                stakePaid = parseFloat((Number(stakePaidWei) / 1e6).toFixed(2));
                break;
              }
            } catch (e) {
              // Skip logs that don't match our ABI
              continue;
            }
          }
        }

        // ===== DATABASE SAVE =====
        await joinChallengeInDB(id, stakePaid);

        // Track successful join
        analytics.track.challengeJoined({
          challengeId: id,
          challengeName: challenge.name,
          challengeType: challenge.type || "run",
          stakeAmount: stakePaid,
          durationDays: challenge.durationDays,
          dailyGoal: challenge.dailyGoal,
        });
        analytics.track.stakeDeposited({
          challengeId: id,
          amount: stakePaid,
          currency: "USDC",
        });

        // Navigate to join success page
        navigate("/challenge-join-success", {
          replace: true,
          state: {
            challengeId: id,
            challengeName: challenge.name,
          },
        });
      } catch (error: any) {
        // Use refined error message
        const errorMessage = refineErrorMessage(error);
        setJoinError(errorMessage);
        setShowJoinErrorModal(true);
      } finally {
        setIsJoining(false);
      }
    });
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col text-content-light dark:text-content-dark pb-40 bg-background-light dark:bg-background-dark">
      {/* Sticky Header with safe-area */}
      <StickyHeader
        transparent
        leftContent={
          <HeaderIconButton
            icon="arrow_back"
            onClick={() => navigate(-1)}
            ariaLabel="Go back"
          />
        }
        title="Challenge Details"
        rightContent={
          <HeaderIconButton
            icon="ios_share"
            variant="subtle"
            onClick={handleShare}
            ariaLabel="Share challenge"
          />
        }
      />

      {/* Main Content */}
      <main className="flex flex-col gap-4 px-4 pt-4">
        {/* Hero Section */}
        <section>
          <h1 className="font-display tracking-tight text-[32px] font-bold leading-tight text-content-light dark:text-content-dark">
            {challenge.name}
          </h1>
          <p className="font-display pt-1 text-base font-normal leading-normal text-content-light/70 dark:text-content-dark/70">
            {getFirstSentence(challenge.description)}
          </p>
          <div className="flex gap-3 pt-4 flex-wrap">
            <div
              className="flex h-8 shrink-0 items-center justify-center gap-x-2 rounded-full px-4"
              style={{ backgroundColor: "rgba(13, 242, 13, 0.2)" }}
            >
              <p
                className="font-display text-sm font-medium leading-normal"
                style={{ color: "#0df20d" }}
              >
                {getChallengeCategory(challenge.type)}
              </p>
            </div>
            {/* Status Badge */}
            {joinability.badgeText && (
              <div
                className="flex h-8 shrink-0 items-center justify-center gap-x-2 rounded-full px-4"
                style={{
                  backgroundColor:
                    joinability.badgeVariant === "success"
                      ? "rgba(34, 197, 94, 0.2)"
                      : joinability.badgeVariant === "warning"
                      ? "rgba(234, 179, 8, 0.2)"
                      : joinability.badgeVariant === "error"
                      ? "rgba(239, 68, 68, 0.2)"
                      : "rgba(59, 130, 246, 0.2)",
                }}
              >
                <p
                  className="font-display text-sm font-medium leading-normal"
                  style={{
                    color:
                      joinability.badgeVariant === "success"
                        ? "#22c55e"
                        : joinability.badgeVariant === "warning"
                        ? "#eab308"
                        : joinability.badgeVariant === "error"
                        ? "#ef4444"
                        : "#3b82f6",
                  }}
                >
                  {joinability.badgeText}
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Hero Image */}
        <section className="w-full">
          <div
            className="w-full h-48 bg-center bg-no-repeat bg-cover flex flex-col justify-end overflow-hidden rounded-3xl"
            style={{ backgroundImage: `url("${MOCK_HERO_IMAGE}")` }}
          />
        </section>

        {/* Stats Grid */}
        <section className="grid grid-cols-2 gap-3 pt-2">
          <ChallengeStatsCard
            icon="calendar_month"
            label="Start Date"
            value={formatShortDate(challenge.startDate)}
          />
          <ChallengeStatsCard
            icon="flag"
            label={challenge.type === "marathon" ? "Distance" : "Daily Goal"}
            value={
              challenge.type === "marathon"
                ? "42.195 KM"
                : `${challenge.dailyGoal} KM / Day`
            }
          />
          <ChallengeStatsCard
            icon="timer"
            label={challenge.type === "marathon" ? "Goal Time" : "Duration"}
            value={
              challenge.type === "marathon" && challenge.targetDurationSeconds
                ? formatTargetTime(challenge.targetDurationSeconds)
                : `${challenge.durationDays} Days`
            }
          />
          <ChallengeStatsCard
            icon="paid"
            label="Stake"
            value={`$${challenge.stakeAmount}`}
            highlighted
          />
        </section>

        {/* Participants */}
        <section className="pt-4">
          <div className="flex items-center">
            <div className="flex -space-x-3">
              {MOCK_PARTICIPANT_AVATARS.map((avatar, index) => (
                <img
                  key={index}
                  className="inline-block h-10 w-10 rounded-full ring-2 ring-background-light dark:ring-background-dark"
                  src={avatar}
                  alt={`Participant ${index + 1}`}
                />
              ))}
            </div>
            <p className="ml-4 font-medium text-content-light dark:text-content-dark">
              {(challenge.participantCount ||
                challenge.participants?.length ||
                0) > 0
                ? `${
                    challenge.participantCount || challenge.participants?.length
                  }+ Challengers Joined`
                : "Be the first to join!"}
            </p>
          </div>
        </section>

        {/* The Story Behind This Challenge */}
        <section className="pt-4">
          <ExpandableSection
            title="The Story Behind This Challenge"
            preview={getFirstSentence(challenge.description)}
          >
            <p className="whitespace-pre-wrap">
              {challenge.description ||
                "Join this challenge and start your fitness journey!"}
            </p>
          </ExpandableSection>
        </section>

        {/* How it Works */}
        <section className="pt-4">
          <ExpandableSection title="How it Works">
            <ul className="list-disc space-y-2 pl-5">
              {getHowItWorks(
                challenge.dailyGoal,
                challenge.durationDays,
                challenge.type,
                challenge.targetDurationSeconds
              ).map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ul>
          </ExpandableSection>
        </section>
      </main>

      {/* Fixed Footer */}
      <footer
        className="fixed bottom-0 left-0 w-full p-4 backdrop-blur-sm border-t border-black/10 dark:border-white/10 bg-background-light/80 dark:bg-background-dark/80"
        style={{
          paddingBottom: "max(1rem, env(safe-area-inset-bottom))",
        }}
      >
        {/* Balance Display */}
        <div className="flex items-center justify-between gap-4 mb-4">
          <div className="flex items-center gap-2">
            <svg
              className="h-6 w-6 text-primary"
              fill="none"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12 2v20"></path>
              <path d="M18.36 6.64a9 9 0 1 1-12.72 0"></path>
            </svg>
            <p className="text-sm font-medium text-content-light/70 dark:text-content-dark/70">
              Your Balance
            </p>
          </div>
          {balanceLoading ? (
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
          ) : (
            <p className="text-lg font-bold text-content-light dark:text-content-dark">
              ${walletBalance.toFixed(2)}
            </p>
          )}
        </div>

        {/* Join Button */}
        <button
          onClick={handleJoinChallenge}
          disabled={isJoining || !joinability.canJoin}
          className="flex w-full cursor-pointer items-center justify-center overflow-hidden rounded-3xl h-14 gap-2 text-lg font-bold leading-normal tracking-wide transition-transform active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed bg-primary text-background-dark shadow-glow-primary"
        >
          {isJoining ? (
            <>
              <LoadingSpinner size="md" className="mr-2" />
              <span>Joining Challenge...</span>
            </>
          ) : !joinability.canJoin ? (
            <>
              <span className="material-symbols-outlined mr-2">
                {joinability.reason === "already_joined"
                  ? "check_circle"
                  : joinability.reason === "already_started"
                  ? "play_circle"
                  : "block"}
              </span>
              <span>{joinability.buttonText}</span>
            </>
          ) : (
            <>{joinability.buttonText}</>
          )}
        </button>
      </footer>

      {/* Strava Connection Prompt */}
      <ConnectStravaPrompt
        isOpen={showPrompt}
        onClose={closePrompt}
        onSuccess={handleConnectionSuccess}
      />

      {/* Insufficient Balance Modal */}
      <InsufficientBalanceModal
        isOpen={showInsufficientBalanceModal}
        onClose={() => setShowInsufficientBalanceModal(false)}
        currentBalance={walletBalance}
        requiredAmount={challenge?.stakeAmount || 0}
        walletAddress={privyUser?.wallet?.address || ""}
      />

      {/* Join Error Modal */}
      <JoinErrorModal
        isOpen={showJoinErrorModal}
        onClose={() => {
          setShowJoinErrorModal(false);
          setJoinError(null);
        }}
        errorMessage={joinError || undefined}
      />

      {/* Toast Notification */}
      <ErrorToast
        isOpen={toastOpen}
        onClose={() => setToastOpen(false)}
        message={toastMessage}
        variant={toastVariant}
        position="top"
        duration={3000}
      />
    </div>
  );
}
